import {
  Card,
  CardCloseButton as CloseButton,
  Flex,
  Modal,
  Spinner,
} from "coral-ui/components/v2";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import styles from "./ImageModal.css";

interface Props {
  open: boolean;
  onClose: () => void;
  imageURL: string;
  imageAlt?: string;
}

const ImageModal: FunctionComponent<Props> = ({
  open,
  onClose,
  imageURL,
  imageAlt = "",
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Reset loading state when imageURL changes
  useEffect(() => {
    if (imageURL) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [imageURL]);

  // Force focus when modal opens
  useEffect(() => {
    if (open && cardRef.current) {
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} data-testid="modal-imageModal">
      {() => (
        <Card
          ref={cardRef}
          className={styles.card}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <Flex
            direction="column"
            alignItems="center"
            className={styles.container}
          >
            <Flex justifyContent="flex-end" className={styles.header}>
              <CloseButton onClick={onClose} className={styles.closeButton} />
            </Flex>
            <Flex className={styles.imageContainer}>
              {hasError ? (
                <div className={styles.errorMessage}>Unable to load image</div>
              ) : (
                <>
                  {isLoading && (
                    <div className={styles.loadingIndicator}>
                      <Spinner size="md" />
                    </div>
                  )}
                  <img
                    src={imageURL}
                    alt={imageAlt || "Image"}
                    className={styles.image}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: isLoading ? "none" : "block" }}
                  />
                </>
              )}
            </Flex>
          </Flex>
        </Card>
      )}
    </Modal>
  );
};

export default ImageModal;
